import * as React from "react";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { defaultOffices } from "../../../../../store/cache/location";
import { useToasts } from "react-toast-notifications";
import {
    ClinicianOfficeWindowRequests,
    DateFormats,
    OfficeRequestStatus,
} from "intake_shared/src/types";
import { AclContext } from "../../../../../contexts/AclContext";
import store from "../../../../../store";
import Table, { TablePagination } from "../../../bits/Table/Table";
import {
    addSpaceToString,
    sortStringsWithNumberEndings,
} from "../../../../../utils/utils";
import { Dropdown } from "../../../bits/Dropdown/Dropdown";
import { Button } from "../../../bits/Button/Button";
import { Badge } from "../../../bits/Badge/Badge";
import { OFFICE_REQUEST_STATUS_COLORS } from "../../../../../constants";
import moment from "moment";
import { TableHeader } from "../../../../../types";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import FilterButton from "../../../bits/FilterButton/FilterButton";
import { uniq } from "lodash";
import RefreshIcon from "../../../icons/Refresh";
import Loader from "../../../bits/Loader/Loader";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

export default function ClinicianDeleteRequests() {
    const { offices: availableOffices = defaultOffices } = React.useContext(
        CacheContext
    ) || {
        offices: defaultOffices,
    };
    const { addToast } = useToasts();
    const [currentPage, setCurrentPage] = React.useState(1);
    const pageLimit = 50;
    const [selectedLocation, setSelectedLocation] = React.useState("");
    const [selectedOfficeNumber, setSelectedOfficeNumber] = React.useState("");
    const [selectedStatus, setSelectedStatus] = React.useState("");
    const [refreshKey, setRefreshKey] = React.useState(0);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [nextButtonDisabled, setNextButtonDisabled] = React.useState(false);
    const [deleteRequests, setDeleteRequests] =
        React.useState<ClinicianOfficeWindowRequests[]>();
    const [loading, setLoading] = React.useState(false);

    const handleLocationChange = (location: string) => {
        setSelectedLocation(location);
        setSelectedOfficeNumber("");
        setCurrentPage(1);
    };

    const handleOfficeChange = (office: string) => {
        setSelectedOfficeNumber(office);
        setCurrentPage(1);
    };

    const handleStatusChange = (status: string) => {
        setSelectedStatus(status);
        setCurrentPage(1);
    };

    const getClinicianDeleteRequests = async () => {
        if (!loggedInUserEmail) return;
        setLoading(true);
        try {
            const requests = await store.findOfficeWindowRequestsByClinicianEmail(
                loggedInUserEmail,
                currentPage,
                pageLimit,
                {
                    location: selectedLocation,
                    office: selectedOfficeNumber,
                    status: selectedStatus,
                    category: "delete_office_window_list",
                }
            );
            setDeleteRequests(requests);
            setNextButtonDisabled(requests.length < pageLimit);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            addToast("An error occurred while fetching deletion requests.", {
                appearance: "error",
                autoDismiss: false,
            });
        }
    };

    React.useEffect(() => {
        getClinicianDeleteRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        loggedInUserEmail,
        refreshKey,
        currentPage,
        selectedLocation,
        selectedOfficeNumber,
        selectedStatus,
    ]);

    const columns = React.useMemo(() => {
        const headers: TableHeader[] = [
            {
                Header: "DATE OF REQUEST",
                id: "request_date",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.request_date).format(
                        DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                    )}`}</p>
                ),
            },
            {
                Header: "REQUESTED DAY",
                id: "request_day",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.start_date).format(
                        DateFormats.DAY
                    )}`}</p>
                ),
            },
            {
                Header: "REQUESTED TIME",
                id: "request_time",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.start_date).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )} - ${moment(row.end_date).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )}`}</p>
                ),
            },
            {
                Header: "LOCATION",
                id: "location",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.location || "--"}</p>
                ),
            },
            {
                Header: "OFFICE",
                id: "office",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.office || "--"}</p>
                ),
            },
            {
                Header: "REASON FOR DELETION",
                id: "reason_for_delete",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <div className="break_word">{row.reason_for_delete || "--"}</div>
                ),
            },
            {
                Header: "STATUS",
                id: "status",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <span>
                        <Badge
                            content={addSpaceToString(row.status || "")}
                            bgColor={OFFICE_REQUEST_STATUS_COLORS[row.status || ""]}
                            textColor="var(--color-white)"
                        />
                    </span>
                ),
            },
            {
                Header: "COMMENTS",
                id: "comments",
                accessor: (row: ClinicianOfficeWindowRequests) => {
                    const comment = row.rejection_reason;
                    if (!comment) return <span>--</span>;
                    return (
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip
                                    id={`comment-tooltip-${row.office_window_request_id}`}
                                >
                                    {comment}
                                </Tooltip>
                            }
                        >
                            <span>
                                {comment.length > 20
                                    ? comment.substring(0, 20) + "..."
                                    : comment}
                            </span>
                        </OverlayTrigger>
                    );
                },
            },
        ];
        return headers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLocationLabel = (location: string) => location || "Select location";
    const getOfficeLabel = (office: string) => office || "Select office";
    const getStatusLabel = (status: string) =>
        addSpaceToString(status) || "Select status";

    const handleResetFilters = () => {
        setSelectedLocation("");
        setSelectedStatus("");
        setSelectedOfficeNumber("");
        setCurrentPage(1);
    };

    const handleFirstPageClick = () => setCurrentPage(1);
    const handlePrevClick = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleNextClick = () => setCurrentPage(currentPage + 1);
    const handleRefreshClick = () => {
        setCurrentPage(1);
        setRefreshKey((prev) => prev + 1);
    };

    const tableFooter = (
        <TablePagination
            currentPage={currentPage}
            onFirstPage={handleFirstPageClick}
            onPrevPage={handlePrevClick}
            onNextPage={handleNextClick}
            isNextDisabled={nextButtonDisabled}
            showPrevButton={currentPage > 1}
        />
    );

    return (
        <div>
            <Loader show={loading} />
            <div className="office-manager-filters">
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getLocationLabel(selectedLocation)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content width="170px">
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedLocation === ""}
                            onClick={() => handleLocationChange("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {Object.keys(availableOffices).map((k: string) => (
                            <Dropdown.Item
                                key={k}
                                closeMenuOnSelect
                                isSelected={selectedLocation === k}
                                onClick={() => handleLocationChange(k)}
                            >
                                {k}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getOfficeLabel(selectedOfficeNumber)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedOfficeNumber === ""}
                            onClick={() => handleOfficeChange("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {sortStringsWithNumberEndings(
                            uniq(availableOffices[selectedLocation] || [])
                        ).map((r) => (
                            <Dropdown.Item
                                key={r}
                                onClick={() => handleOfficeChange(r)}
                                isSelected={selectedOfficeNumber === r}
                            >
                                {r}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getStatusLabel(selectedStatus)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content width="190px">
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedStatus === ""}
                            onClick={() => handleStatusChange("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {Object.values(OfficeRequestStatus)
                            .filter(
                                (s) =>
                                    s === OfficeRequestStatus.APPROVED ||
                                    s === OfficeRequestStatus.REJECTED ||
                                    s === OfficeRequestStatus.PENDING
                            )
                            .map((s) => (
                                <Dropdown.Item
                                    key={`status-${s}`}
                                    closeMenuOnSelect
                                    isSelected={selectedStatus === s}
                                    onClick={() => handleStatusChange(s)}
                                >
                                    {addSpaceToString(s)}
                                </Dropdown.Item>
                            ))}
                    </Dropdown.Content>
                </Dropdown>
                <Button onClick={handleRefreshClick}>
                    <RefreshIcon />
                </Button>
                <Button
                    variant="normal"
                    className="reset-filters-button"
                    onClick={handleResetFilters}
                >
                    Reset Filters
                </Button>
            </div>
            {deleteRequests &&
                (deleteRequests.length === 0 ? (
                    <EmptyListState
                        emptyMessage="No deletion requests found"
                        stateHelperText="Your office window deletion requests will appear here"
                    />
                ) : (
                    <Table
                        columns={columns}
                        data={deleteRequests}
                        allowSelect={false}
                        footer={
                            currentPage > 1 || !nextButtonDisabled
                                ? tableFooter
                                : null
                        }
                    />
                ))}
        </div>
    );
}
